import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BotId, DialogId, IntentId, StepId } from '@feedyou/schema'
import _ from 'lodash'
import { BotFeatures } from '@feedyou-ai/feedbot-schema'
import { BotHeaderTab } from '../../features/botHeaderTabs/types'
import {
  areTheSameTab,
  createDialogTab,
  isTabContainedInList,
  removeTabsFromList
} from '../../features/botHeaderTabs'
import { DELETE_DIALOGS } from '../../constants/actionTypes'

export interface NewAppSliceState {
  openBotId: BotId | undefined
  updateHostingLoading: boolean
  updateHostingError?: string
  sdkFeatures: BotFeatures[]

  selectedHeaderTab: {
    tab: BotHeaderTab | undefined | null
    pendingScrollId?: StepId | IntentId
  }
  // Note that some tabs are not closable, and thus always open, even when not present here
  openHeaderTabs: BotHeaderTab[]
}

export const initialState: NewAppSliceState = {
  openBotId: undefined,
  updateHostingLoading: false,
  sdkFeatures: [], // it is possible to preset some BotFeatures during development here, need to clear local storage to load

  // Note that some tabs are not closable, and thus always open, even when not present here
  openHeaderTabs: [],
  selectedHeaderTab: {
    tab: null,
    pendingScrollId: undefined
  }
}

const slice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    updateHosting: state => {
      state.updateHostingLoading = true
      state.updateHostingError = undefined
    },
    updateHostingSuccess: state => {
      state.updateHostingLoading = false
    },
    updateHostingFailure: (state, action) => {
      state.updateHostingLoading = false
      state.updateHostingError = action.payload
    },

    selectTab: (state, action: PayloadAction<{ tab: BotHeaderTab; pendingScrollId?: string }>) => {
      state.selectedHeaderTab.tab = action.payload.tab
      state.selectedHeaderTab.pendingScrollId = action.payload.pendingScrollId
    },

    clearPendingScrollForSelectedTab: state => {
      state.selectedHeaderTab.pendingScrollId = undefined
    },

    openNewTab: (state, action: PayloadAction<{ tab: BotHeaderTab }>) => {
      const tabToOpen = action.payload.tab
      state.openHeaderTabs = _.uniqWith([...state.openHeaderTabs, tabToOpen], areTheSameTab)
    },

    closeTabs: (state, action: PayloadAction<BotHeaderTab[]>) => {
      const tabsToClose = action.payload

      state.openHeaderTabs = removeTabsFromList(tabsToClose, state.openHeaderTabs)

      if (tabsToClose.some(tab => isTabContainedInList(tab, state.openHeaderTabs))) {
        // We should react to this state declaratively by re-opening some tab
        state.selectedHeaderTab.tab = null
      }
    },

    setOpenBotId: (state, action: PayloadAction<BotId | undefined>) => {
      state.openBotId = action.payload
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      action => action === DELETE_DIALOGS,
      (state, action: PayloadAction<DialogId[]>) => {
        const tabsForDeletedDialogs: BotHeaderTab[] = action.payload.map(createDialogTab)

        state.openHeaderTabs = removeTabsFromList(state.openHeaderTabs, tabsForDeletedDialogs)
      }
    )
  }
})

export const reducer = slice.reducer
export const appActions = slice.actions
